import { SocketRecovery } from "@/cache/recovery"
import ModelManager from "@/services/model-manager"
import {
  type CommandHandler,
  type ModelUpdateHandler,
  type SocketMessageObserver,
  type SocketHandler
} from "@/interfaces/socket/socket-handlers.interface"
import { type ModelCreateHandler } from "@/interfaces/socket/socket-handlers.interface"
import { type ModelDeleteHandler } from "@/interfaces/socket/socket-handlers.interface"
import { type IBase } from "@/interfaces/api/base.interface"
import DataHelper from "@/helper/data-helper"
import type { ILockData, IReleaseData } from "@/interfaces/socket/lock-data.interface"
import { useLockStore } from "@/store/lock-store"
import {
  type IIncomingSocketMessage,
  IncomingSocketMessageTypes
} from "@/interfaces/socket/incoming-socket-message.interface"
import GlobalsService from "@/services/globals-service"
import { useSynchronisationStore } from "@/store/synchronisation.store.ts"
import { storeToRefs } from "pinia"
import type { ICommandData } from "@/interfaces/socket/command-data.interface.ts"
import { CentrifugoTokenRepository } from "@/repositories/centrifugo-token-repository.ts"
import {
  Centrifuge,
  State as CentrifugeState,
  type Subscription,
  SubscriptionState,
  UnauthorizedError
} from "centrifuge"
import { App } from "@capacitor/app"
import type { PluginListenerHandle } from "@capacitor/core"
import { metrics as SentryMetrics } from "@sentry/capacitor"
import { isProductionEnvironment } from "@/helper/app/browser-helper.ts"
import { SyncOutageReporter, type SyncPhase } from "./sync-outage-reporter"

/**
 * High-level lifecycle state of the synchronisation WebSocket.
 *
 * Single source of truth for live-update readiness, derived from both the
 * connection and its update subscription inside `SocketService`.
 */
export enum SocketState {
  // The connection and update subscription are both ready to receive messages.
  CONNECTED = "connected",
  // Socket is closed or closing for any reason other than a known, recoverable one.
  DISCONNECTED = "disconnected",
  // Connection, subscription or initial token acquisition is being retried.
  SERVICE_RESTARTING = "service_restarting"
}

export default class SocketService<DataType extends IBase> {
  private static _instance: SocketService<IBase>
  private centrifuge: Centrifuge | null = null
  private centrifugoSubscription: Subscription | null = null
  private isInitializingCentrifugo = false
  private centrifugoRetryTimer: ReturnType<typeof setTimeout> | null = null
  private isCentrifugoUnauthorized = false
  private readonly syncOutageReporter = new SyncOutageReporter()
  private isIncomingMessageLoggingEnabled = false

  public static getInstance() {
    if (!this._instance) {
      this._instance = new SocketService()

      // Outside production, hand the singleton to the page so an end-to-end test can register its
      // own handler and count what actually reaches the handler stage. Delivery counted from the
      // outside (WebSocket frames, or a second observer connection) proves only that Centrifugo
      // sent something; it says nothing about whether this client, on this CPU, kept up with
      // decoding and dispatching it. See playwright/10-realtime-slow-device.spec.ts.
      //
      // Nothing is exposed that page scripts could not already reach by importing the module.
      if (!isProductionEnvironment() || import.meta.env.VITE_PLAYWRIGHT_TEST_HOOKS === "true") {
        ;(window as unknown as Record<string, unknown>).__socketService = this._instance
      }
    }
    return this._instance
  }

  modelCreateHandlers: ModelCreateHandler<DataType>[]
  modelUpdateHandlers: ModelUpdateHandler<DataType>[]
  modelDeleteHandlers: ModelDeleteHandler<DataType>[]
  commandHandlers: CommandHandler[]
  private readonly messageObservers = new Set<SocketMessageObserver>()

  modelsForWhichToIgnoreSelfMessages: { [key: string]: boolean | undefined }

  private lockStore
  private readonly synchronisationStore

  private recovery = new SocketRecovery(
    (message) => this.dispatchCentrifugoPublication(message),
    () => this.centrifugoChain,
    async (model, deleted, data) => {
      let handlers = this.modelUpdateHandlers
      if (deleted) handlers = this.modelDeleteHandlers
      else if (!ModelManager.getInstance().exists(data.id!)) handlers = this.modelCreateHandlers
      await Promise.all(
        handlers
          .filter((handler) => handler.model === model)
          .map((handler) => handler.handler(data as DataType))
      )
    }
  )

  private centrifugoTokenRepo: CentrifugoTokenRepository
  private appResumeListener: PluginListenerHandle | null = null

  constructor() {
    this.lockStore = useLockStore()
    this.synchronisationStore = useSynchronisationStore()

    this.centrifugoTokenRepo = new CentrifugoTokenRepository()

    this.modelCreateHandlers = []
    this.modelUpdateHandlers = []
    this.modelDeleteHandlers = []
    this.commandHandlers = []
    this.modelsForWhichToIgnoreSelfMessages = {}
    this.initializeSocket()

    const { socketIsAlive } = storeToRefs(this.synchronisationStore)
    void App.addListener("resume", () => {
      if (!socketIsAlive.value) {
        console.log("App became visible; attempting to re-establish socket connection")
        this.initializeSocket()
      }
    }).then((listener) => {
      this.appResumeListener = listener
    })
  }

  private async initializeSocket(): Promise<void> {
    if (!import.meta.env.VITE_CENTRIFUGO_URL) {
      console.warn(
        "VITE_CENTRIFUGO_URL is not configured; real-time synchronisation is disabled. " +
          "Centrifugo is the only transport since the Channels WebSocket was retired (PNTS-3019)."
      )
      return
    }
    await this.initializeCentrifugo()
  }

  /**
   * Centrifugo transport. Connects to Centrifugo instead of the legacy Channels WebSocket and
   * feeds publications into the same routing pipeline as `establishConnection`.
   *
   * The tenant channel is subscribed CLIENT-SIDE (authorized by a per-channel subscription
   * token the backend only mints for the caller's own tenant) rather than embedded in the
   * connection token, because delta compression is only available to client-side
   * subscriptions. Reconnection, backoff, token refresh, missed-message recovery and delta
   * decoding are all handled natively by the Centrifuge client.
   */
  private async initializeCentrifugo(): Promise<void> {
    // A terminal subscription failure can leave the transport connected. Resume must revive
    // both; subscribe()/connect() are no-ops when already subscribed/connected/connecting.
    if (this.centrifuge) {
      this.isCentrifugoUnauthorized = false
      this.syncOutageReporter.retry(
        this.centrifuge.state === CentrifugeState.Connected ? "subscription" : "connection"
      )
      this.centrifugoSubscription?.subscribe()
      this.centrifuge.connect()
      return
    }
    if (this.isInitializingCentrifugo) {
      return
    }

    this.isInitializingCentrifugo = true
    this.isCentrifugoUnauthorized = false
    this.syncOutageReporter.retry("initialization")
    this.setCentrifugoSocketState(SocketState.SERVICE_RESTARTING, "initialization")
    if (this.centrifugoRetryTimer !== null) {
      clearTimeout(this.centrifugoRetryTimer)
      this.centrifugoRetryTimer = null
    }
    try {
      await this.createCentrifugoClient()
    } catch (error) {
      const tokenError = error as { status?: number; response?: { status?: number } } | null
      this.syncOutageReporter.error(
        "initialization",
        tokenError?.response?.status ?? tokenError?.status
      )
      // The SDK cannot retry until it exists. The HTTP layer only retries a request once, so
      // keep trying here if the initial token request still fails (network, 500, 502, etc.).
      console.warn(
        "Centrifugo initialization failed; retrying in 5s:",
        error instanceof Error ? error.message : String(error)
      )
      this.centrifugoRetryTimer = setTimeout(() => {
        this.centrifugoRetryTimer = null
        void this.initializeCentrifugo()
      }, 5000)
    } finally {
      this.isInitializingCentrifugo = false
    }
  }

  private async createCentrifugoClient(): Promise<void> {
    // The initial token fetch also names the tenant channel, which is needed up front to create
    // the client-side subscription. Transient failures retry here; 401/403 means this session
    // cannot use the socket at all (revoked, or not a planner), so give up quietly.
    const { token, channel, status, changeNotifications, retainedObjects } =
      await this.centrifugoTokenRepo.getConnectionToken()
    if (!token || !channel) {
      if (status === 401 || status === 403) {
        this.recovery.release()
        this.isCentrifugoUnauthorized = true
        this.setCentrifugoSocketState(SocketState.DISCONNECTED, "initialization")
        this.syncOutageReporter.error("initialization", status)
        console.warn(`Centrifugo connection token unauthorized (status ${status}); not retrying`)
        return
      }
      throw Object.assign(
        new Error(`Centrifugo connection token request failed (status ${status})`),
        { status }
      )
    }

    const endpoint = `${import.meta.env.VITE_CENTRIFUGO_URL}/connection/websocket`

    const centrifuge = new Centrifuge(endpoint, {
      token,
      getToken: async () => {
        this.syncOutageReporter.retry("connection")
        const { token: fresh, status: refreshStatus } =
          await this.centrifugoTokenRepo.getConnectionToken()
        if (fresh) {
          return fresh
        }
        // 401/403 means this session can never get a token (revoked, or not a planner). Tell
        // centrifuge-js to stop reconnecting instead of hammering the token endpoint forever.
        if (refreshStatus === 401 || refreshStatus === 403) {
          this.isCentrifugoUnauthorized = true
          this.syncOutageReporter.cancel()
          throw new UnauthorizedError(
            `Centrifugo token request unauthorized (status ${refreshStatus})`
          )
        }
        // Anything else (503 not-configured, 5xx, network) is transient: let centrifuge-js retry.
        throw new Error(`Centrifugo connection token request failed (status ${refreshStatus})`)
      }
    })

    centrifuge.on("connected", () => {
      this.updateCentrifugoSocketState()
      console.log("[open] Centrifugo connection established")
    })

    // 'connecting' fires on the first connect and on every transient reconnect. Map it to
    // SERVICE_RESTARTING ("quick reconnect expected") rather than DISCONNECTED so the desync
    // warning is held back during the grace period instead of flashing on startup or brief blips.
    centrifuge.on("connecting", (ctx) => {
      this.updateCentrifugoSocketState()
      console.log(`[connecting] Centrifugo connecting, code=${ctx.code} reason=${ctx.reason}`)
    })

    // 'disconnected' is terminal (centrifuge-js has stopped retrying) -> surface the warning now.
    centrifuge.on("disconnected", (ctx) => {
      this.setCentrifugoSocketState(SocketState.DISCONNECTED, "connection")
      this.syncOutageReporter.error("connection", ctx.code)
      // ctx.code is Centrifuge's own disconnect-code namespace (not WebSocket close codes); tag the
      // transport so this stays distinguishable from the legacy WebSocket metric in dashboards.
      SentryMetrics.count("socket_desync.disconnect", 1, {
        attributes: { code: ctx.code, transport: "centrifugo" }
      })
      console.log(`[close] Centrifugo disconnected, code=${ctx.code} reason=${ctx.reason}`)
    })

    centrifuge.on("error", (ctx) => {
      this.syncOutageReporter.error("connection", ctx.error.code)
      console.log("[error] Centrifugo error", ctx)
    })

    centrifuge.connect() // Connect while the recovery asks Django what changed; subscribe once it knows where from.
    const since = await this.recovery.open(channel, changeNotifications, retainedObjects)
    let hasRequestedSubscriptionToken = false
    const subscription = centrifuge.newSubscription(channel, {
      since,
      // Fossil delta compression: the server sends binary diffs against the previous publication
      // for the repeated full-model payloads on this channel. Requires delta_publish +
      // allowed_delta_types=fossil in the server channel config.
      delta: "fossil",
      getToken: async () => {
        if (hasRequestedSubscriptionToken) {
          this.syncOutageReporter.retry("subscription")
        }
        hasRequestedSubscriptionToken = true
        const { token: subToken, status: subStatus } =
          await this.centrifugoTokenRepo.getSubscriptionToken(channel)
        if (subToken) {
          return subToken
        }
        if (subStatus === 401 || subStatus === 403) {
          this.isCentrifugoUnauthorized = true
          this.syncOutageReporter.cancel()
          throw new UnauthorizedError(
            `Centrifugo subscription token unauthorized (status ${subStatus})`
          )
        }
        throw new Error(`Centrifugo subscription token request failed (status ${subStatus})`)
      }
    })

    // The tenant channel has force_recovery + force_positioning on the server, so missed
    // publications are replayed from channel history on reconnect. When the gap exceeds the
    // retained history, recovery fails (`recovered: false`) - the same situation as the legacy
    // "buffer depleted" 404, so it triggers the same unrecoverable flow (DesynchronisationWindow
    // -> delayed page refresh).
    subscription.on("subscribed", (ctx) => {
      this.recovery.subscribed(ctx)
      this.updateCentrifugoSocketState(ctx.wasRecovering ? ctx.recovered : null)
      console.log(
        `[subscribed] Centrifugo channel ${ctx.channel}, wasRecovering=${ctx.wasRecovering} recovered=${ctx.recovered}`
      )
      if (ctx.wasRecovering && !ctx.recovered) {
        SentryMetrics.count("socket_desync.unrecoverable", 1, {
          attributes: { transport: "centrifugo" }
        })
        this.synchronisationStore.isMessagesUnrecoverable = true
      }
    })

    subscription.on("subscribing", (ctx) => {
      this.recovery.pause()
      this.updateCentrifugoSocketState()
      console.log(
        `[subscribing] Centrifugo channel ${ctx.channel}, code=${ctx.code} reason=${ctx.reason}`
      )
    })

    subscription.on("unsubscribed", (ctx) => {
      this.updateCentrifugoSocketState()
      this.syncOutageReporter.error("subscription", ctx.code)
      SentryMetrics.count("socket_desync.disconnect", 1, {
        attributes: { code: ctx.code, transport: "centrifugo", phase: "subscription" }
      })
      console.warn(
        `[unsubscribed] Centrifugo channel ${ctx.channel}, code=${ctx.code} reason=${ctx.reason}`
      )
    })

    subscription.on("error", (ctx) => {
      this.syncOutageReporter.error("subscription", ctx.error.code)
      console.warn("[error] Centrifugo subscription error", ctx)
    })

    subscription.on("publication", (ctx) => this.recovery.publication(ctx))

    this.centrifuge = centrifuge
    this.centrifugoSubscription = subscription
    subscription.subscribe()
  }

  private updateCentrifugoSocketState(historyRecovered: boolean | null = null): void {
    const connectionState = this.centrifuge?.state
    const subscriptionState = this.centrifugoSubscription?.state
    if (
      connectionState === CentrifugeState.Disconnected ||
      subscriptionState === SubscriptionState.Unsubscribed
    ) {
      this.setCentrifugoSocketState(
        SocketState.DISCONNECTED,
        connectionState === CentrifugeState.Disconnected ? "connection" : "subscription"
      )
    } else if (
      connectionState === CentrifugeState.Connected &&
      subscriptionState === SubscriptionState.Subscribed
    ) {
      this.setCentrifugoSocketState(SocketState.CONNECTED, "subscription", historyRecovered)
    } else {
      this.setCentrifugoSocketState(
        SocketState.SERVICE_RESTARTING,
        connectionState === CentrifugeState.Connected ? "subscription" : "connection"
      )
    }
  }

  private setCentrifugoSocketState(
    state: SocketState,
    phase: SyncPhase,
    historyRecovered: boolean | null = null
  ): void {
    this.synchronisationStore.socketState = state
    if (state === SocketState.CONNECTED) {
      this.syncOutageReporter.ready(historyRecovered)
    } else if (this.isCentrifugoUnauthorized) {
      // Revoked/forbidden sessions are terminal auth failures, not service outages. Keep
      // diagnostics stopped across subsequent SDK state events until an explicit resume.
      this.syncOutageReporter.cancel()
    } else {
      this.syncOutageReporter.start(phase)
    }
  }

  // ── Centrifugo dispatch ───────────────────────────────────────────────────
  //
  // Everything the old SocketQueue did is now the broker's job. Publications arrive in publish
  // order on one connection, positioning detects loss server-side, and recovery replays missed
  // history, so the reordering, expiration timers, gap detection and HTTP buffer backfill are gone
  // along with the message numbers they ran on.
  //
  // Duplicates are handled at the source too: CentrifugoPublisher mints one idempotency_key per
  // message and reuses it across retries, so Centrifugo drops the duplicate before it is ever
  // published. No client-side dedupe is needed.
  //
  // What remains is the one thing a broker cannot do: keep async handlers (bulk applies) from
  // interleaving. That is a promise chain.

  private centrifugoChain: Promise<void> = Promise.resolve()

  private centrifugoQueueNameFor(type: IncomingSocketMessageTypes): string | null {
    if (
      [
        IncomingSocketMessageTypes.MODEL_CREATE,
        IncomingSocketMessageTypes.MODEL_UPDATE,
        IncomingSocketMessageTypes.MODEL_DELETE
      ].includes(type)
    ) {
      return "synchronisation"
    }
    if (
      [IncomingSocketMessageTypes.MODEL_LOCK, IncomingSocketMessageTypes.MODEL_RELEASE].includes(
        type
      )
    ) {
      return "locking"
    }
    if (type === IncomingSocketMessageTypes.COMMAND) {
      return "command"
    }
    // STATUS pulses only existed to carry last-seen message numbers for gap detection. Positioning
    // and recovery replace them, and the backend no longer sends them at all.
    return null
  }

  private dispatchCentrifugoPublication(rawMessage: any) {
    const msg = DataHelper.convertDateStringsToMoment(rawMessage)
    const queueName = this.centrifugoQueueNameFor(msg.type)
    if (queueName === null) {
      return
    }

    this.centrifugoChain = this.centrifugoChain
      .then(() => this.handleIncomingMessage(msg))
      .catch((err) => {
        this.recovery.invalidate()
        console.error(`[centrifugo] Handler failed for queue '${queueName}'`, err, msg)
      })
  }

  public setIncomingMessageLogging(enabled: boolean) {
    this.isIncomingMessageLoggingEnabled = enabled
  }

  private async handleIncomingMessage(msg: IIncomingSocketMessage<DataType | undefined>) {
    const isSelfMessage = msg.sender === GlobalsService.getInstance().getMySessionUUID()
    const isIgnoredSelfMessage =
      isSelfMessage && this.modelsForWhichToIgnoreSelfMessages[msg.details.model]

    this.notifyMessageObservers(msg, isSelfMessage, Boolean(isIgnoredSelfMessage))

    if (this.isIncomingMessageLoggingEnabled) {
      console.log("[socket] Incoming message", {
        message: msg,
        isSelfMessage,
        isIgnoredSelfMessage
      })
    }

    if (isIgnoredSelfMessage) {
      // Ignore messages sent by myself.
      return
    }

    if (msg.details.isBulk) {
      await this.handleBulkAction(msg)
    } else if (msg.type === IncomingSocketMessageTypes.MODEL_CREATE) {
      this.handleModelCreate(msg.details.model, msg.details.data as DataType)
    } else if (msg.type === IncomingSocketMessageTypes.MODEL_UPDATE) {
      this.handleModelUpdate(msg.details.model, msg.details.data as DataType)
    } else if (msg.type === IncomingSocketMessageTypes.MODEL_DELETE) {
      this.handleModelDelete(msg.details.model, msg.details.data as DataType)
    } else if (msg.type === IncomingSocketMessageTypes.MODEL_LOCK) {
      this.handleModelLock(msg.details.model, msg.details.data as ILockData)
    } else if (msg.type === IncomingSocketMessageTypes.MODEL_RELEASE) {
      this.handleModelRelease(msg.details.model, msg.details.data as IReleaseData)
    } else if (msg.type === IncomingSocketMessageTypes.COMMAND) {
      this.handleCommand(msg.details.model, msg.details.data as ICommandData)
    }
  }

  private notifyMessageObservers(
    message: IIncomingSocketMessage<DataType | undefined>,
    isSelfMessage: boolean,
    isIgnoredSelfMessage: boolean
  ) {
    for (const observer of this.messageObservers) {
      try {
        observer({
          message: message as IIncomingSocketMessage<IBase | undefined>,
          isSelfMessage,
          isIgnoredSelfMessage
        })
      } catch (error) {
        // Observers are passive side effects and must never interrupt socket synchronization.
        console.error("[socket] Message observer failed", error)
      }
    }
  }

  private async handleBulkAction(msg: IIncomingSocketMessage<DataType | undefined>) {
    // Bulk payloads always travel inline: Centrifugo channel history retains full publications
    // (the legacy null-data announcement + HTTP buffer refetch was retired with the buffer).
    const normalizedData = msg.details.data
    if (!normalizedData) {
      console.error("Bulk message without inline payload:", msg)
      return
    }

    const models = DataHelper.denormalizeData(normalizedData)

    await this.applyBulkModels(msg.type, msg.details.model, models)
  }

  /**
   * Applies a list of models to their handlers as a single bulk. Synchronisation progress belongs
   * to the socket-recovery lifecycle, so normal bulk messages do not open the recovery overlay.
   */
  private async applyBulkModels(
    type: IncomingSocketMessageTypes,
    model: string,
    models: DataType[]
  ) {
    // Process in chunks so Vue and the browser can repaint between chunks.
    const chunkSize = 25
    for (let start = 0; start < models.length; start += chunkSize) {
      const chunk = models.slice(start, start + chunkSize)
      for (const m of chunk) {
        if (type === IncomingSocketMessageTypes.MODEL_CREATE) {
          this.handleModelCreate(model, m)
        } else if (type === IncomingSocketMessageTypes.MODEL_UPDATE) {
          this.handleModelUpdate(model, m)
        } else if (type === IncomingSocketMessageTypes.MODEL_DELETE) {
          this.handleModelDelete(model, m)
        }
      }
      if (start + chunk.length < models.length) {
        await this.waitForFrame()
      }
    }
  }

  private waitForFrame(): Promise<void> {
    return new Promise((resolve) => requestAnimationFrame(() => resolve()))
  }

  public addModelCreateHandler(modelCreateHandler: ModelCreateHandler<IBase>) {
    this.modelCreateHandlers.push(modelCreateHandler)
  }

  public addModelUpdateHandler(modelUpdateHandler: ModelUpdateHandler<IBase>) {
    this.modelUpdateHandlers.push(modelUpdateHandler)
  }

  public addModelDeleteHandler(modelDeleteHandler: ModelDeleteHandler<IBase>) {
    this.modelDeleteHandlers.push(modelDeleteHandler)
  }

  public addCommandHandler(commandHandler: CommandHandler) {
    this.commandHandlers.push(commandHandler)
    return commandHandler
  }

  public addMessageObserver(observer: SocketMessageObserver) {
    this.messageObservers.add(observer)
    return () => this.removeMessageObserver(observer)
  }

  public removeMessageObserver(observer: SocketMessageObserver) {
    this.messageObservers.delete(observer)
  }

  public removeCommandHandler(commandHandler: CommandHandler) {
    this.removeHandler(commandHandler, this.commandHandlers)
  }

  public removeHandler(handler: SocketHandler, collection: SocketHandler[]) {
    const index = collection.indexOf(handler)
    if (index !== -1) {
      collection.splice(index, 1)
    }
  }

  public ignoreSelfMessagesForModel(model: string) {
    this.modelsForWhichToIgnoreSelfMessages[model] = true
  }

  private handleModelCreate(model: string, data: DataType) {
    const modelCreateHandlers = this.modelCreateHandlers.filter(
      (modelCreateHandler: ModelCreateHandler<DataType>) => modelCreateHandler.model === model
    )
    for (const modelCreateHandler of modelCreateHandlers) {
      modelCreateHandler.handler(data)
    }
  }

  private handleModelUpdate(model: string, data: DataType) {
    const modelUpdateHandlers = this.modelUpdateHandlers.filter(
      (modelUpdateHandler: ModelUpdateHandler<DataType>) => modelUpdateHandler.model === model
    )
    for (const modelUpdateHandler of modelUpdateHandlers) {
      modelUpdateHandler.handler(data)
    }
  }

  private handleModelDelete(model: string, data: DataType) {
    const modelDeleteHandlers = this.modelDeleteHandlers.filter(
      (modelDeleteHandler: ModelDeleteHandler<DataType>) => modelDeleteHandler.model === model
    )
    for (const modelDeleteHandler of modelDeleteHandlers) {
      modelDeleteHandler.handler(data)
    }
  }

  private handleCommand(command: string, data: ICommandData) {
    const commandHandlers = this.commandHandlers.filter(
      (commandHandler: CommandHandler) => commandHandler.command === command
    )
    for (const commandHandler of commandHandlers) {
      commandHandler.handler(data)
    }
  }

  private handleModelLock(model: string, data: ILockData) {
    const key = `${model}${data.id}`
    this.lockStore.lockLocal(key, data.sessionUuid)
  }

  private handleModelRelease(model: string, data: IReleaseData) {
    const key = `${model}${data.id}`
    this.lockStore.releaseLocal(key)
  }
}
